import React, { useState, useEffect, useRef, useCallback } from 'react';
import './NotificationBell.css';

const TYPE_ICON = { info: 'ℹ️', success: '✅', warning: '⚠️', error: '🔴' };
const POLL_MS = 30_000;

export default function NotificationBell({ onNavigate }) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const fetchCount = useCallback(async () => {
    try {
      const r = await fetch('/api/notifications/unread-count');
      if (r.ok) setCount((await r.json()).count || 0);
    } catch (_) {}
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch('/api/notifications?limit=20');
      if (r.ok) setItems(await r.json());
    } catch (_) {}
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, POLL_MS);
    return () => clearInterval(id);
  }, [fetchCount]);

  useEffect(() => {
    if (!open) return;
    fetchItems();
  }, [open, fetchItems]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const markRead = async (id) => {
    await fetch(`/api/notifications/${id}/read`, { method: 'POST' });
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    setCount((c) => Math.max(0, c - 1));
  };

  const markAllRead = async () => {
    await fetch('/api/notifications/read-all', { method: 'POST' });
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setCount(0);
  };

  const handleItemClick = async (n) => {
    if (!n.is_read) await markRead(n.id);
    if (n.action_view && onNavigate) {
      onNavigate(n.action_view);
      setOpen(false);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diffM = Math.floor((now - d) / 60000);
    if (diffM < 1) return 'just now';
    if (diffM < 60) return `${diffM}m ago`;
    if (diffM < 1440) return `${Math.floor(diffM / 60)}h ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="nb-wrap" ref={ref}>
      <button
        className="nb-btn"
        onClick={() => setOpen((o) => !o)}
        aria-label={`Notifications${count ? ` (${count} unread)` : ''}`}
        title="Notifications"
      >
        <span className="nb-icon">🔔</span>
        {count > 0 && <span className="nb-badge">{count > 99 ? '99+' : count}</span>}
      </button>

      {open && (
        <div className="nb-drawer">
          <div className="nb-drawer__header">
            <span className="nb-drawer__title">Notifications</span>
            {count > 0 && (
              <button className="nb-mark-all" onClick={markAllRead}>
                Mark all read
              </button>
            )}
          </div>

          <div className="nb-drawer__list">
            {loading && <div className="nb-empty">Loading…</div>}
            {!loading && items.length === 0 && (
              <div className="nb-empty">You're all caught up!</div>
            )}
            {!loading && items.map((n) => (
              <button
                key={n.id}
                className={`nb-item${n.is_read ? ' nb-item--read' : ''}`}
                onClick={() => handleItemClick(n)}
              >
                <span className="nb-item__icon">{TYPE_ICON[n.type] || 'ℹ️'}</span>
                <div className="nb-item__body">
                  <div className="nb-item__title">{n.title}</div>
                  {n.body && <div className="nb-item__sub">{n.body}</div>}
                  <div className="nb-item__time">{formatTime(n.created_at)}</div>
                </div>
                {!n.is_read && <span className="nb-item__dot" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
